import * as React from 'react';
import type { IdentityOcrResult, MisafirGirdi } from '@hotelcms/shared';
import { ApiHatasi, api } from '@/lib/api';
import { paraFormatla } from '@/lib/utils';
import { vknGecerliMi } from '@hotelcms/shared';
import { KimlikYakala } from '@/features/checkin/KimlikYakala';
import { ocrdanMisafir } from '@/features/checkin/MisafirFormu';

export interface OdaSecenegi {
  id: string;
  no: string;
  roomTypeId: string;
  roomTypeAd: string | null;
  durum: 'BOS' | 'DOLU' | 'ARIZALI' | 'BLOKE';
}

/** Modalin acilisinda hazir gelen degerler (bos hucreye dokununca dolar). */
export interface YeniRezervasyonHazir {
  roomId?: string | null;
  girisTarihi?: string;
  cikisTarihi?: string;
}

interface Props {
  odalar: OdaSecenegi[];
  hazir: YeniRezervasyonHazir;
  yetkiler: string[];
  /** Istemci tarafi cakisma kontrolu — sunucuya gitmeden uyarabilmek icin. */
  cakisiyorMu: (roomId: string, giris: string, cikis: string) => boolean;
  onKapat: () => void;
  onOlusturuldu: (rezId: string, checkInBaslat: boolean) => void;
}

const bugunIso = () => new Date().toISOString().slice(0, 10);
const gunEkle = (iso: string, n: number) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const geceSayisi = (a: string, b: string) =>
  Math.round((new Date(`${b}T00:00:00Z`).getTime() - new Date(`${a}T00:00:00Z`).getTime()) / 86_400_000);

const KAYNAKLAR = [
  ['WALK_IN', 'Kapıdan geldi'],
  ['TELEFON', 'Telefon'],
  ['DIREKT', 'Direkt'],
  ['WEB', 'Web'],
  ['ACENTA', 'Acenta'],
] as const;

const PANSIYONLAR = [
  ['BB', 'Oda + kahvaltı'],
  ['HB', 'Yarım pansiyon'],
  ['FB', 'Tam pansiyon'],
  ['AI', 'Her şey dahil'],
  ['RO', 'Sadece oda'],
] as const;

/**
 * Yeni rezervasyon penceresi.
 *
 * Iki yoldan doldurulur:
 *   1) "Kimliği okut" -> kamera/dosya -> OCR alanlari doldurur (kapidan gelen misafir)
 *   2) Elle: ad + soyad yeter, gerisi istege bagli
 *
 * Zorunlu alan sayisi bilerek en aza indirildi: oda, tarih, ad, soyad.
 * Geri kalani (pansiyon, tutar, not) acilir "Detaylar" bolumunde durur.
 */
export function YeniRezervasyon({ odalar, hazir, yetkiler, cakisiyorMu, onKapat, onOlusturuldu }: Props) {
  const [roomId, setRoomId] = React.useState<string>(hazir.roomId ?? odalar[0]?.id ?? '');
  const [giris, setGiris] = React.useState<string>(hazir.girisTarihi ?? bugunIso());
  const [cikis, setCikis] = React.useState<string>(
    hazir.cikisTarihi ?? gunEkle(hazir.girisTarihi ?? bugunIso(), 1),
  );
  const [yetiskin, setYetiskin] = React.useState(2);
  const [cocuk, setCocuk] = React.useState(0);
  const [misafir, setMisafir] = React.useState<MisafirGirdi>(() => ocrdanMisafir(null));
  const [kaynak, setKaynak] = React.useState<string>('WALK_IN');
  const [pansiyon, setPansiyon] = React.useState<string>('BB');
  const [tutar, setTutar] = React.useState<string>('');
  const [not, setNot] = React.useState('');
  const [detayAcik, setDetayAcik] = React.useState(false);
  const [kaporaAlindi, setKaporaAlindi] = React.useState(false);
  const [kaporaTutar, setKaporaTutar] = React.useState('');
  const [kaporaTip, setKaporaTip] = React.useState<'NAKIT' | 'HAVALE' | 'KREDI_KARTI'>('NAKIT');
  const [kurumsal, setKurumsal] = React.useState(false);
  const [unvan, setUnvan] = React.useState('');
  const [vergiNo, setVergiNo] = React.useState('');
  const [vergiDairesi, setVergiDairesi] = React.useState('');
  const [faturaEposta, setFaturaEposta] = React.useState('');
  const [tarama, setTarama] = React.useState(false);
  const [tarife, setTarife] = React.useState<{ toplamTutar: number | null; gecelikTutar: number | null; eksikGun: string[] } | null>(null);
  const [tutarElle, setTutarElle] = React.useState(false);
  const [ocrOzet, setOcrOzet] = React.useState<string | null>(null);
  const [hata, setHata] = React.useState<string | null>(null);
  const [gonderiliyor, setGonderiliyor] = React.useState(false);

  React.useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && !gonderiliyor && onKapat();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onKapat, gonderiliyor]);

  const oda = odalar.find((o) => o.id === roomId) ?? null;
  const gece = geceSayisi(giris, cikis);

  /**
   * Oda tipi veya tarih degisince tesisin tarifesini cek.
   *
   * Onceden fiyat ezberden yaziliyordu; artik takvimden gelen tutar onerilir
   * ve "bir gecelik" kapora kisayolu da bu tarifeden hesaplanir. Kullanici
   * tutari elle degistirdiyse uzerine yazmayiz.
   */
  React.useEffect(() => {
    if (!oda || gece < 1) {
      setTarife(null);
      return;
    }
    let iptal = false;
    api<{ toplamTutar: number | null; gecelikTutar: number | null; eksikGun: string[] }>(
      `/api/rezervasyonlar/tarife?roomTypeId=${encodeURIComponent(oda.roomTypeId)}&girisTarihi=${giris}&cikisTarihi=${cikis}`,
    )
      .then((t) => {
        if (iptal) return;
        setTarife(t);
        if (!tutarElle && t.toplamTutar !== null && t.toplamTutar > 0) setTutar(String(t.toplamTutar));
      })
      .catch(() => !iptal && setTarife(null));
    return () => {
      iptal = true;
    };
  }, [oda?.roomTypeId, giris, cikis, gece, tutarElle, oda]);
  const tarihGecerli = gece > 0;
  const tutarSayisi = Number(tutar.replace(',', '.')) || 0;
  const cakisma = oda && tarihGecerli ? cakisiyorMu(oda.id, giris, cikis) : false;
  const kaporaSayi = Number(kaporaTutar.replace(',', '.'));
  const kaporaGecerli = kaporaAlindi && Number.isFinite(kaporaSayi) && kaporaSayi > 0;
  // VKN'nin kontrol hanesi burada dogrulanir; yanlis VKN faturayi GIB'den geri dondurur.
  const vknTamam = vknGecerliMi(vergiNo);
  const kurumsalTamam = !kurumsal || (unvan.trim().length >= 2 && vknTamam);
  /**
   * Bir gecelik ucret: kucuk otellerde kaporanin fiili standardi.
   * Once tesisin tarife takvimi, o yoksa girilen toplamin gece basina payi.
   */
  const birGece =
    tarife?.gecelikTutar != null
      ? Math.round(tarife.gecelikTutar)
      : gece > 0 && tutarSayisi > 0
        ? Math.round(tutarSayisi / gece)
        : 0;

  const gonderilebilir =
    !!oda &&
    tarihGecerli &&
    !cakisma &&
    kurumsalTamam &&
    misafir.ad.trim() !== '' &&
    misafir.soyad.trim() !== '';

  function ocrGeldi(sonuc: IdentityOcrResult) {
    setMisafir(ocrdanMisafir(sonuc));
    const eksik = sonuc.dogrulama.kbsEksikAlanlar;
    setOcrOzet(
      eksik.length > 0
        ? `Kimlik okundu. KBS bildirimi için eksik: ${eksik.join(', ')}`
        : 'Kimlik okundu, KBS için gereken alanlar tamam.',
    );
    setTarama(false);
  }

  async function gonder() {
    if (!gonderilebilir || !oda) return;
    setGonderiliyor(true);
    setHata(null);
    try {
      const tutarSayi = tutar.trim() === '' ? null : Number(tutar.replace(',', '.'));
      const cevap = await api<{ id: string }>('/api/rezervasyonlar', {
        method: 'POST',
        body: JSON.stringify({
          girisTarihi: giris,
          cikisTarihi: cikis,
          roomTypeId: oda.roomTypeId,
          roomId: oda.id,
          yetiskin,
          cocuk,
          kaynak,
          pansiyon,
          toplamTutar: Number.isFinite(tutarSayi) ? tutarSayi : null,
          not: not.trim() === '' ? null : not.trim(),
          misafirler: [{ ...misafir, anaMisafir: true }],
          kapora: kaporaGecerli
            ? { tutar: Number(kaporaTutar.replace(',', '.')), tip: kaporaTip, referans: null }
            : undefined,
          kurumsalFatura: kurumsal
            ? {
                unvan: unvan.trim(),
                vergiNo: vergiNo.trim(),
                vergiDairesi: vergiDairesi.trim() || null,
                eposta: faturaEposta.trim() || null,
              }
            : undefined,
        }),
      });
      // Kimlik zaten okunduysa dogruca check-in'e gecmenin anlami var.
      onOlusturuldu(cevap.id, ocrOzet !== null && yetkiler.includes('checkin:perform'));
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Rezervasyon oluşturulamadı.');
      setGonderiliyor(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-lacivert/60 [animation:fadein_.2s_both]"
      onClick={() => !gonderiliyor && onKapat()}
      role="presentation"
    >
      {/* Ic sarmalayici: pencere ekrandan uzunsa ortalanmis kalirken kaydirilabilir olur. */}
      <div className="flex min-h-full items-center justify-center p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="yeni-rez-baslik"
        onClick={(e) => e.stopPropagation()}
        className={
          'w-full rounded-[20px] bg-white shadow-[0_40px_80px_-32px_rgba(11,21,48,.55)] [animation:popin_.3s_cubic-bezier(.2,.8,.2,1)_both] ' +
          (tarama ? 'max-w-[900px]' : 'max-w-[560px]')
        }
      >
        <div className="flex items-start justify-between gap-4 border-b border-cizgi px-6 py-5">
          <div>
            <h2 id="yeni-rez-baslik" className="text-[20px] font-bold tracking-[-.01em] text-lacivert">
              {tarama ? 'Kimliği okut' : 'Yeni rezervasyon'}
            </h2>
            <p className="mt-0.5 text-[13px] text-gri">
              {tarama
                ? 'Okunan bilgiler forma otomatik geçer.'
                : 'Oda, tarih ve misafir adı yeterli. Gerisi isteğe bağlı.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onKapat}
            disabled={gonderiliyor}
            aria-label="Kapat"
            className="grid size-8 shrink-0 place-items-center rounded-full bg-zemin text-lg leading-none text-gri hover:bg-cizgi"
          >
            ×
          </button>
        </div>

        {tarama ? (
          <div className="p-6">
            <KimlikYakala onSonuc={ocrGeldi} onManuel={() => setTarama(false)} />
          </div>
        ) : (
          <div className="flex flex-col gap-4 px-6 py-5">
            {yetkiler.includes('identity:ocr') && (
              <button
                type="button"
                onClick={() => setTarama(true)}
                className="flex items-center gap-3 rounded-[14px] border-[1.5px] border-dashed border-mavi-acik bg-mavi-zemin px-4 py-3.5 text-left transition hover:border-marka-600"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1f4fd8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M3 8V5.5A2.5 2.5 0 0 1 5.5 3H8m8 0h2.5A2.5 2.5 0 0 1 21 5.5V8m0 8v2.5a2.5 2.5 0 0 1-2.5 2.5H16m-8 0H5.5A2.5 2.5 0 0 1 3 18.5V16" />
                  <path d="M7 12h10" stroke="#f2b632" />
                </svg>
                <span className="min-w-0 flex-1">
                  <span className="block text-[15px] font-semibold text-lacivert">Kimliği okut</span>
                  <span className="block text-[13px] text-gri">Ad, soyad ve KBS alanları kendiliğinden dolar</span>
                </span>
                <span className="text-[13px] font-semibold text-marka-600">Aç →</span>
              </button>
            )}

            {ocrOzet && (
              <p className="rounded-xl bg-yesil-zemin px-3.5 py-3 text-[13px] font-medium text-yesil">{ocrOzet}</p>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <Alan etiket="Ad">
                <input
                  value={misafir.ad}
                  onChange={(e) => setMisafir({ ...misafir, ad: e.target.value })}
                  autoFocus
                  className={girdiSinifi}
                />
              </Alan>
              <Alan etiket="Soyad">
                <input
                  value={misafir.soyad}
                  onChange={(e) => setMisafir({ ...misafir, soyad: e.target.value })}
                  className={girdiSinifi}
                />
              </Alan>
            </div>

            <Alan etiket="Oda">
              <select value={roomId} onChange={(e) => setRoomId(e.target.value)} className={girdiSinifi}>
                {odalar.map((o) => (
                  <option key={o.id} value={o.id} disabled={o.durum === 'ARIZALI' || o.durum === 'BLOKE'}>
                    {o.no} · {o.roomTypeAd ?? 'Tip yok'}
                    {o.durum === 'ARIZALI' ? ' (arızalı)' : o.durum === 'BLOKE' ? ' (bloke)' : ''}
                  </option>
                ))}
              </select>
            </Alan>

            <div className="grid gap-3 sm:grid-cols-2">
              <Alan etiket="Giriş">
                <input
                  type="date"
                  value={giris}
                  onChange={(e) => {
                    setGiris(e.target.value);
                    if (e.target.value >= cikis) setCikis(gunEkle(e.target.value, 1));
                  }}
                  className={girdiSinifi}
                />
              </Alan>
              <Alan etiket="Çıkış" ipucu={tarihGecerli ? `${gece} gece` : undefined}>
                <input type="date" value={cikis} min={gunEkle(giris, 1)} onChange={(e) => setCikis(e.target.value)} className={girdiSinifi} />
              </Alan>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Alan etiket="Yetişkin">
                <input type="number" min={1} max={10} value={yetiskin} onChange={(e) => setYetiskin(Math.max(1, Number(e.target.value) || 1))} className={girdiSinifi} />
              </Alan>
              <Alan etiket="Çocuk">
                <input type="number" min={0} max={10} value={cocuk} onChange={(e) => setCocuk(Math.max(0, Number(e.target.value) || 0))} className={girdiSinifi} />
              </Alan>
            </div>

            {cakisma && (
              <p className="rounded-xl bg-[#fdeceb] px-3.5 py-3 text-[13px] font-medium text-kirmizi">
                Oda {oda?.no} bu tarihlerde dolu. Başka oda ya da tarih seçin.
              </p>
            )}
            {!tarihGecerli && (
              <p className="rounded-xl bg-[#fdeceb] px-3.5 py-3 text-[13px] font-medium text-kirmizi">
                Çıkış tarihi giriş tarihinden sonra olmalı.
              </p>
            )}

            {/* Kapora: kucuk otel/apartlarda telefonla alinan rezervasyonun
                neredeyse standardi. Kutucuk degil, gercek tahsilat kaydi. */}
            {yetkiler.includes('payment:write') && (
              <div className="flex flex-col gap-3 rounded-[14px] border-[1.5px] border-cizgi p-4">
                <label className="flex cursor-pointer items-center gap-2.5">
                  <input
                    type="checkbox"
                    checked={kaporaAlindi}
                    onChange={(e) => {
                      setKaporaAlindi(e.target.checked);
                      if (e.target.checked && !kaporaTutar && birGece > 0) setKaporaTutar(String(birGece));
                    }}
                    className="peer sr-only"
                  />
                  <span className="grid size-5 flex-none place-items-center rounded-[6px] border-[1.5px] border-cizgi bg-white transition peer-checked:border-marka-600 peer-checked:bg-marka-600">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="m5 12.5 4.5 4.5L19 7" />
                    </svg>
                  </span>
                  <span className="text-[15px] font-semibold text-lacivert">Kapora alındı</span>
                </label>

                {kaporaAlindi && (
                  <div className="flex flex-col gap-3">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Alan etiket="Kapora tutarı (TL)">
                        <input
                          inputMode="decimal"
                          value={kaporaTutar}
                          onChange={(e) => setKaporaTutar(e.target.value)}
                          placeholder="0"
                          className={girdiSinifi}
                        />
                      </Alan>
                      <Alan etiket="Tahsilat şekli">
                        <select
                          value={kaporaTip}
                          onChange={(e) => setKaporaTip(e.target.value as typeof kaporaTip)}
                          className={girdiSinifi}
                        >
                          <option value="NAKIT">Nakit</option>
                          <option value="HAVALE">Havale / EFT</option>
                          <option value="KREDI_KARTI">Kredi kartı</option>
                        </select>
                      </Alan>
                    </div>

                    {birGece > 0 && (
                      <button
                        type="button"
                        onClick={() => setKaporaTutar(String(birGece))}
                        className="self-start rounded-full bg-mavi-zemin px-3 py-1.5 text-[13px] font-semibold text-marka-600 transition hover:bg-cizgi"
                      >
                        Bir gecelik: {paraFormatla(birGece * 100, 'TRY')}
                      </button>
                    )}

                    <p className="text-[12.5px] leading-relaxed text-gri">
                      Kapora kasaya girer ve misafirin hesabından düşer; çıkışta kalan bakiye tahsil edilir.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Tuzel kisi faturasi: rezervasyonu firma yapiyorsa bilgi SIMDI alinir,
                cikista misafir beklerken vergi dairesi telefonla aranmasin. */}
            <div className="flex flex-col gap-3 rounded-[14px] border-[1.5px] border-cizgi p-4">
              <label className="flex cursor-pointer items-center gap-2.5">
                <input
                  type="checkbox"
                  checked={kurumsal}
                  onChange={(e) => setKurumsal(e.target.checked)}
                  className="peer sr-only"
                />
                <span className="grid size-5 flex-none place-items-center rounded-[6px] border-[1.5px] border-cizgi bg-white transition peer-checked:border-marka-600 peer-checked:bg-marka-600">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="m5 12.5 4.5 4.5L19 7" />
                  </svg>
                </span>
                <span className="text-[15px] font-semibold text-lacivert">Fatura firmaya kesilecek</span>
              </label>

              {kurumsal && (
                <div className="flex flex-col gap-3">
                  <Alan etiket="Firma unvanı">
                    <input
                      value={unvan}
                      onChange={(e) => setUnvan(e.target.value)}
                      placeholder="Ege Yazılım A.Ş."
                      className={girdiSinifi}
                    />
                  </Alan>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Alan
                      etiket="Vergi kimlik no (VKN)"
                      ipucu={vergiNo.length === 10 ? (vknTamam ? 'geçerli' : 'hatalı') : '10 hane'}
                    >
                      <input
                        inputMode="numeric"
                        value={vergiNo}
                        onChange={(e) => setVergiNo(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        placeholder="1234567890"
                        className={
                          girdiSinifi + (vergiNo.length === 10 && !vknTamam ? ' border-kirmizi focus:border-kirmizi' : '')
                        }
                      />
                    </Alan>
                    <Alan etiket="Vergi dairesi" ipucu="İsteğe bağlı">
                      <input value={vergiDairesi} onChange={(e) => setVergiDairesi(e.target.value)} className={girdiSinifi} />
                    </Alan>
                  </div>
                  <Alan etiket="Fatura e-postası" ipucu="İsteğe bağlı">
                    <input
                      type="email"
                      value={faturaEposta}
                      onChange={(e) => setFaturaEposta(e.target.value)}
                      placeholder="muhasebe@firma.com"
                      className={girdiSinifi}
                    />
                  </Alan>
                  {vergiNo.length === 10 && !vknTamam && (
                    <p className="rounded-xl bg-[#fdeceb] px-3.5 py-3 text-[13px] font-medium text-kirmizi">
                      Bu VKN'nin kontrol hanesi tutmuyor. Yanlış VKN ile kesilen fatura GİB'den geri döner.
                    </p>
                  )}
                  <p className="text-[12.5px] leading-relaxed text-gri">
                    Konaklayan misafir yine yukarıdaki kişidir; KBS bildirimi ona göre yapılır. Buradaki bilgi
                    yalnızca faturada kullanılır.
                  </p>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => setDetayAcik((v) => !v)}
              className="self-start text-[13.5px] font-semibold text-marka-600 hover:underline"
            >
              {detayAcik ? 'Detayları gizle' : 'Detaylar (kaynak, pansiyon, tutar, not)'}
            </button>

            {detayAcik && (
              <div className="flex flex-col gap-3 rounded-[14px] bg-zemin p-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <Alan etiket="Kaynak">
                    <select value={kaynak} onChange={(e) => setKaynak(e.target.value)} className={girdiSinifi}>
                      {KAYNAKLAR.map(([d, m]) => (
                        <option key={d} value={d}>{m}</option>
                      ))}
                    </select>
                  </Alan>
                  <Alan etiket="Pansiyon">
                    <select value={pansiyon} onChange={(e) => setPansiyon(e.target.value)} className={girdiSinifi}>
                      {PANSIYONLAR.map(([d, m]) => (
                        <option key={d} value={d}>{m}</option>
                      ))}
                    </select>
                  </Alan>
                </div>
                <Alan etiket="Toplam tutar (TL)" ipucu="Boş bırakılabilir">
                  <input
                    inputMode="decimal"
                    value={tutar}
                    onChange={(e) => {
                      setTutar(e.target.value);
                      setTutarElle(true);
                    }}
                    placeholder="0"
                    className={girdiSinifi}
                  />
                </Alan>
                <Alan etiket="Not">
                  <textarea value={not} onChange={(e) => setNot(e.target.value)} rows={2} className={girdiSinifi} />
                </Alan>
              </div>
            )}

            {hata && (
              <p role="alert" className="rounded-xl bg-[#fdeceb] px-3.5 py-3 text-[13px] font-medium text-kirmizi">{hata}</p>
            )}

            <div className="mt-1 flex gap-3">
              <button
                type="button"
                onClick={onKapat}
                disabled={gonderiliyor}
                className="flex-1 rounded-full border-[1.5px] border-cizgi bg-white py-3 text-[15px] font-semibold text-lacivert transition hover:border-gri"
              >
                Vazgeç
              </button>
              <button
                type="button"
                onClick={gonder}
                disabled={!gonderilebilir || gonderiliyor}
                className="flex flex-1 items-center justify-center gap-2 rounded-full bg-marka-600 py-3 text-[15px] font-semibold text-white transition hover:bg-marka-700 disabled:opacity-40"
              >
                {gonderiliyor && (
                  <span className="size-4 rounded-full border-2 border-white/35 border-t-white [animation:spin_.7s_linear_infinite]" />
                )}
                {gonderiliyor ? 'Kaydediliyor…' : 'Rezervasyonu oluştur'}
              </button>
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

const girdiSinifi =
  'w-full rounded-xl border-[1.5px] border-cizgi bg-white px-3.5 py-2.5 text-[15px] text-lacivert outline-none transition focus:border-marka-600 focus:shadow-[0_0_0_4px_rgba(31,79,216,.12)]';

function Alan({ etiket, ipucu, children }: { etiket: string; ipucu?: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="flex items-baseline justify-between gap-2 text-[12.5px] font-semibold text-gri">
        {etiket}
        {ipucu && <span className="font-medium text-marka-600">{ipucu}</span>}
      </span>
      {children}
    </label>
  );
}
